import { DataTable, DataTableColumnDef } from 'components/data-table';
import { Link } from 'components/link';
import { Section } from 'components/section';
import { DocumentResponse } from 'types/api-responses';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';
import styles from './royalty-check-tables.module.scss';
import type { LineItemColumn, LineItemRow, WellCitationRow } from './types';

const RC_DOCUMENT_TYPE = 'Document::RoyaltyCheck';
const RC_TARGET_PROVISION_FIELD = 'royalty_check_target';

const wellColumns: DataTableColumnDef<WellCitationRow>[] = [
  {
    id: 'provisionValue',
    accessorKey: 'provisionValue',
    header: 'Wells From Document',
    meta: { type: 'text' },
  },
  {
    id: 'matchedWells',
    header: 'Matched Wells',
    enableSorting: false,
    meta: { type: 'text', wrap: true },
    // Mirror the previous CitationMatchTable match cell: link each resolved
    // well (+ UWI), or an em dash when nothing matched.
    cell: ({ row }) => {
      const targets = row.original.regulatedTargets ?? [];
      if (targets.length === 0) return '—';

      return targets.map((target) => (
        <div key={target.id}>
          <Link to={target.href}>{target.displayName}</Link>
          {target.uwi && (
            <div className={styles.matchMeta}>UWI: {target.uwi}</div>
          )}
          <div className={styles.matchMeta}>Target Type: Well</div>
        </div>
      ));
    },
  },
];

const RoyaltyCheckWellsCitations = ({
  document,
}: {
  document: DocumentResponse;
}) => {
  if (document.type !== RC_DOCUMENT_TYPE) return null;

  const wellCitations =
    document.citations?.filter(
      (citation) => citation.provisionField === RC_TARGET_PROVISION_FIELD
    ) ?? [];
  if (wellCitations.length === 0) return null;

  return (
    <Section header="Well Information">
      {/* Cap height + scroll the row body (page-level only). See
          royalty-check-tables.module.scss. */}
      <div className={styles.scrollTable}>
        <DataTable<WellCitationRow>
          data={wellCitations as WellCitationRow[]}
          columns={wellColumns}
          getRowId={(row) => String(row.id)}
          scroll={{ mode: 'paginated' }}
          initialTableState={{ pageSize: DEFAULT_PAGE_SIZE }}
          config={{ export: false, columnFilters: false }}
        />
      </div>
    </Section>
  );
};

const LINE_ITEM_COLUMNS: LineItemColumn[] = [
  {
    key: 'operatorName',
    label: 'Operator Name',
    hrefKey: 'operatorEntityHref',
  },
  { key: 'ownerName', label: 'Owner Name', hrefKey: 'ownerEntityHref' },
  { key: 'ownerNumber', label: 'Owner Number' },
  { key: 'checkNumber', label: 'Check Number' },
  { key: 'checkDate', label: 'Check Date' },
  { key: 'checkAmount', label: 'Check Amount', numeric: true },
  { key: 'propertyNumber', label: 'Property Id' },
  { key: 'propertyName', label: 'Property Name' },
  { key: 'wellApiUwi', label: 'API / UWI' },
  { key: 'product', label: 'Product' },
  { key: 'productionDateOriginal', label: 'Production Date' },
  { key: 'productionDateStandardized', label: 'Production Date (Std)' },
  { key: 'interestTypeRaw', label: 'Interest Type' },
  { key: 'interestTypeNormalized', label: 'Interest Type (Norm)' },
  { key: 'taxOrDeductCode', label: 'Tax/Deduct Code' },
  { key: 'ownerInterest', label: 'Owner Interest', numeric: true },
  {
    key: 'distributionInterest',
    label: 'Distribution Interest',
    numeric: true,
  },
  { key: 'propertyBtu', label: 'BTU Factor', numeric: true },
  { key: 'propertyVolume', label: 'Property Volume', numeric: true },
  { key: 'propertyPrice', label: 'Price', numeric: true },
  { key: 'propertyValue', label: 'Property Value', numeric: true },
  {
    key: 'propertyTaxesAndDeductions',
    label: 'Property Taxes & Deducts',
    numeric: true,
  },
  {
    key: 'propertyAmountAfterTaxesAndDeductions',
    label: 'Property Amt After T&D',
    numeric: true,
  },
  { key: 'ownerVolume', label: 'Owner Volume', numeric: true },
  { key: 'ownerValue', label: 'Owner Value', numeric: true },
  {
    key: 'ownerTaxesAndDeductions',
    label: 'Owner Taxes & Deducts',
    numeric: true,
  },
  {
    key: 'ownerAmountAfterTaxesAndDeductions',
    label: 'Owner Amt After T&D',
    numeric: true,
  },
  { key: 'notes', label: 'Notes' },
];

// Values arrive pre-formatted as strings from the presenter, so every column
// is `text` (no re-formatting); numeric columns are just right-aligned.
const columns: DataTableColumnDef<LineItemRow>[] = LINE_ITEM_COLUMNS.map(
  (col) => ({
    id: col.key,
    accessorKey: col.key,
    header: col.label,
    meta: { type: 'text', align: col.numeric ? 'right' : 'left' },
    ...(col.hrefKey
      ? {
          cell: ({ row }) => {
            const value = (row.original[col.key] as string | null) ?? null;
            if (!value) return '—';
            const href = (row.original[col.hrefKey!] as string | null) ?? null;
            return href ? <Link to={href}>{value}</Link> : value;
          },
        }
      : {}),
  })
);

const RoyaltyCheckLineItemsTable = ({
  document,
}: {
  document: DocumentResponse;
}) => {
  const lineItems =
    document.type === RC_DOCUMENT_TYPE ? (document.lineItems ?? []) : [];

  if (document.type !== RC_DOCUMENT_TYPE) return null;
  if (lineItems.length === 0) return null;

  return (
    <Section header="Royalty Check Information">
      {/* Cap height + scroll the row body (page-level only); keeps the default
          page size. See royalty-check-tables.module.scss. */}
      <div className={styles.scrollTable}>
        <DataTable<LineItemRow>
          data={lineItems as LineItemRow[]}
          columns={columns}
          getRowId={(row) => row.path ?? String(row.completionItemId)}
          scroll={{ mode: 'paginated' }}
          initialTableState={{ pageSize: DEFAULT_PAGE_SIZE }}
          config={{ export: false, columnFilters: false }}
        />
      </div>
    </Section>
  );
};

export { RoyaltyCheckLineItemsTable, RoyaltyCheckWellsCitations };
